// helix-exception: dense badge text drawn at 8-11px in Figma has no Helix type-scale step (F-12)
import type { ReactNode } from 'react'
import { CardEmptyState } from './CardEmptyState'
import { CLICKABLE_CARD_SURFACE_CLASS, editButtonHoverFillClass, withStopPropagation } from './cardSurface'
import { FormCard, StatusPill } from './FormCard'
import { CalendarMonth, Envelope, Folder, Pen, Phone, UserCircle } from 'flowbite-react-icons/outline'
import type { ParentPhenotypeLabels } from './parentPhenotypeLabels'

function formatDobForDisplay(dob: string | undefined): string {
  const t = dob?.trim()
  if (!t) return '—'
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return `${m[2]}/${m[3]}/${m[1]}`
  return t
}

interface FamilyCardProps {
  title: string
  hasData?: boolean
  contactInfo?: {
    name?: string
    /** ISO date (YYYY-MM-DD) or display string */
    age?: string
    email?: string
    phone?: string
    medicalRecordNumber?: string
    sampleType?: 'whole-blood' | 'buccal-swab' | 'saliva'
  }
  onEdit?: () => void
  statusMessage?: string
  statusVariant?: 'success' | 'info' | 'neutral'
  needsAttention?: boolean
  /** When provided, shows Key / Additional / Pertinent negatives columns (Figma R5). */
  phenotypeLabels?: ParentPhenotypeLabels
  /** Shown when present (e.g. clinically affected parent notes). */
  clinicalNotes?: string
  /** Clinically affected status label (e.g. "Affected"); shown alongside Sample type. */
  clinicallyAffected?: string
  /** Distinct pill when parent is primary guardian. */
  showPrimaryGuardianBadge?: boolean
}

function PrimaryGuardianBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border-brand-subtle dark:border-brand-800 bg-background-brand-softer dark:bg-gray-800 px-2.5 py-1 text-xs font-medium text-text-fg-info dark:text-brand-300">
      <UserCircle className="h-3.5 w-3.5 shrink-0 text-text-fg-info dark:text-brand-300" aria-hidden />
      {children}
    </span>
  )
}

function PhenotypeColumn({ title, labels }: { title: string; labels: string[] }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      <p className="text-sm font-medium text-text-heading dark:text-white">{title}</p>
      {labels.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {labels.map((label, i) => (
            <li key={`${label}-${i}`} className="text-base leading-6 text-text-body dark:text-gray-400">
              {label}
            </li>
          ))}
        </ul>
      ) : (
        <CardEmptyState />
      )}
    </div>
  )
}

export function FamilyCard({
  title,
  hasData = false,
  contactInfo,
  onEdit,
  statusMessage,
  statusVariant = 'info',
  needsAttention = false,
  phenotypeLabels,
  clinicalNotes,
  clinicallyAffected,
  showPrimaryGuardianBadge = false,
}: FamilyCardProps) {
  const cardClasses = needsAttention
    ? 'order-card-attention !p-4 sm:!p-7'
    : 'order-card !p-4 sm:!p-7'
  const isClickable = Boolean(onEdit)

  const dobDisplay = formatDobForDisplay(contactInfo?.age)
  const mrnDisplay = contactInfo?.medicalRecordNumber?.trim() || '—'
  const emailTrim = contactInfo?.email?.trim()
  const phoneTrim = contactInfo?.phone?.trim()
  const showPhenotypeSection = Boolean(phenotypeLabels)

  return (
    <div
      className={`${cardClasses} w-full min-w-0 shadow-sm${isClickable ? ` ${CLICKABLE_CARD_SURFACE_CLASS}` : ''}`}
      onClick={isClickable ? onEdit : undefined}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <h2 className="flex-1 text-2xl font-semibold leading-8 tracking-tight text-text-heading dark:text-white">
            {title}
          </h2>
          {onEdit && (
            <button
              type="button"
              onClick={withStopPropagation(onEdit)}
              className={`inline-flex items-center justify-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium transition-colors sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-xs ${
                needsAttention
                  ? 'bg-brand-600 text-white hover:bg-brand-700'
                  : 'border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800'
              } ${editButtonHoverFillClass(needsAttention)}`}
              aria-label={`Edit ${title}`}
            >
              <Pen className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
              Edit
            </button>
          )}
        </div>

        {showPrimaryGuardianBadge ? (
          <div className="flex flex-wrap gap-2">
            <PrimaryGuardianBadge>Primary guardian</PrimaryGuardianBadge>
          </div>
        ) : null}

        {hasData && contactInfo ? (
          <div className="flex flex-col gap-4">
            {contactInfo.name?.trim() ? (
              <p className="text-lg font-bold leading-7 text-text-body dark:text-gray-400">{contactInfo.name.trim()}</p>
            ) : null}

            <div className="grid w-full grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
              <span className="flex min-w-0 items-center gap-1 text-sm text-text-heading dark:text-white">
                {emailTrim ? (
                  <>
                    <Envelope className="h-4 w-4 shrink-0 text-text-body dark:text-gray-400" aria-hidden />
                    <span className="min-w-0 truncate leading-5" title={emailTrim}>
                      {emailTrim}
                    </span>
                  </>
                ) : null}
              </span>
              <span className="flex min-w-0 items-center gap-1 text-sm text-text-heading dark:text-white">
                {phoneTrim ? (
                  <>
                    <Phone className="h-4 w-4 shrink-0 text-text-body dark:text-gray-400" aria-hidden />
                    <span className="min-w-0 truncate leading-5" title={phoneTrim}>
                      {phoneTrim}
                    </span>
                  </>
                ) : null}
              </span>
              <span className="flex min-w-0 items-center gap-1 text-sm font-medium text-text-heading dark:text-white">
                <CalendarMonth className="h-4 w-4 shrink-0 text-text-body dark:text-gray-400" aria-hidden />
                <span className="min-w-0 truncate leading-5">
                  <span className="text-text-body-subtle dark:text-gray-500">DOB </span>
                  <span className="text-text-body dark:text-gray-400">{dobDisplay}</span>
                </span>
              </span>
              <span className="flex min-w-0 items-center gap-1 text-sm font-medium text-text-heading dark:text-white">
                <Folder className="h-4 w-4 shrink-0 text-text-body dark:text-gray-400" aria-hidden />
                <span className="min-w-0 truncate leading-5">
                  <span className="text-text-body-subtle dark:text-gray-500">MRN </span>
                  <span className="text-text-body dark:text-gray-400">{mrnDisplay}</span>
                </span>
              </span>
            </div>

            {contactInfo.sampleType || clinicallyAffected?.trim() ? (
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-text-body dark:text-gray-400">
                {contactInfo.sampleType ? (
                  <p>
                    <span className="font-medium text-text-heading dark:text-white">Sample type: </span>
                    {contactInfo.sampleType === 'buccal-swab'
                      ? 'Buccal Swab'
                      : contactInfo.sampleType === 'saliva'
                        ? 'Saliva'
                        : 'Whole Blood'}
                  </p>
                ) : null}
                {clinicallyAffected?.trim() ? (
                  <p>
                    <span className="font-medium text-text-heading dark:text-white">Clinically affected: </span>
                    {clinicallyAffected.trim()}
                  </p>
                ) : null}
              </div>
            ) : null}

            {clinicalNotes?.trim() || showPhenotypeSection ? (
              <>
                <div className="border-t border-border-base dark:border-gray-700" />
                {clinicalNotes?.trim() ? (
                  <div className="flex flex-col gap-1.5 pt-4">
                    <p className="text-sm font-medium text-text-heading dark:text-white">Comparator clinical summary</p>
                    <p className="whitespace-pre-wrap text-sm leading-5 text-text-body dark:text-gray-400">
                      {clinicalNotes.trim()}
                    </p>
                  </div>
                ) : null}
                {showPhenotypeSection && phenotypeLabels ? (
                  <div
                    className={`flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3 ${clinicalNotes?.trim() ? 'pt-2' : 'pt-4'}`}
                  >
                    <PhenotypeColumn title="Key Phenotypes" labels={phenotypeLabels.key} />
                    <PhenotypeColumn title="Additional Phenotypes" labels={phenotypeLabels.additional} />
                    <PhenotypeColumn title="Pertinent Negatives" labels={phenotypeLabels.pertinentNegative} />
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        ) : (
          <CardEmptyState />
        )}

        {statusMessage ? <StatusPill text={statusMessage} variant={statusVariant} /> : null}
      </div>
    </div>
  )
}

interface FamilyHistoryCardProps {
  summary?: string
  details?: string
  hasData?: boolean
  onEdit?: () => void
  needsAttention?: boolean
  /** R5: required section — show the purple left accent bar even when complete. */
  required?: boolean
  statusMessage?: string
  statusVariant?: 'success' | 'info' | 'neutral'
  showEmptyState?: boolean
}

export function FamilyHistoryCard({
  summary,
  details,
  hasData = false,
  onEdit,
  needsAttention = false,
  required = false,
  statusMessage,
  statusVariant = 'info',
  showEmptyState = true,
}: FamilyHistoryCardProps) {
  return (
    <FormCard
      title="Family History"
      onEdit={onEdit}
      needsAttention={needsAttention}
      required={required}
      completed={!needsAttention}
      clickableSurface
    >
      {hasData ? (
        <div className="flex flex-col gap-2">
          {summary && (
            <p className="text-sm font-medium leading-5 text-text-body dark:text-gray-400 sm:text-base sm:leading-6">{summary}</p>
          )}
          {details && <p className="text-sm text-text-body-subtle dark:text-gray-500">{details}</p>}
        </div>
      ) : showEmptyState ? (
        <CardEmptyState />
      ) : null}
      {statusMessage && <StatusPill text={statusMessage} variant={statusVariant} />}
    </FormCard>
  )
}
